<script module lang="ts">
  type Listener = (nowMs: number) => void;
  const listeners = new Set<Listener>();
  let timer: ReturnType<typeof setInterval> | null = null;

  function subscribe(listener: Listener) {
    listeners.add(listener);
    if (timer === null) {
      timer = setInterval(() => {
        const nowMs = Date.now();
        for (const notify of listeners) notify(nowMs);
      }, 1_000);
    }
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0 && timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };
  }
</script>

<script lang="ts">
  import { onMount } from "svelte";
  import { ageAnchor, ageTier, payloadBudgetS, payloadRoot, rowAgeAt } from "./dataAge";

  let { payload, fetchedAtMs, door, root = null, servedOnly = false }: {
    payload: unknown;
    fetchedAtMs: number | null;
    door: string;
    root?: string | null;
    servedOnly?: boolean;
  } = $props();

  let nowMs = $state(Date.now());
  onMount(() => subscribe((tickMs) => (nowMs = tickMs)));

  let agePayload = $derived(servedOnly && payload !== null && typeof payload === "object"
    ? {
        age_s: (payload as Record<string, unknown>).age_s,
        budget_s: (payload as Record<string, unknown>).budget_s,
        root: (payload as Record<string, unknown>).root,
      }
    : payload);
  let anchor = $derived(ageAnchor(agePayload));
  let age = $derived(rowAgeAt(anchor, fetchedAtMs, nowMs));
  let budgetS = $derived(payloadBudgetS(payload));
  let tier = $derived(ageTier(age.ageS, budgetS));
  let integerAge = $derived(age.ageS === null ? null : Math.floor(age.ageS));
  let shown = $derived(age.clock === "future" ? "FUTURE" : formatVisibleAge(integerAge));
  let servedRoot = $derived(root ?? payloadRoot(payload));

  function formatVisibleAge(ageS: number | null): string {
    if (ageS === null) return "NO AGE";
    if (ageS < 120) return `${ageS}s`;
    if (ageS < 7_200) return `${Math.floor(ageS / 60)}m`;
    return `${Math.floor(ageS / 3_600)}h`;
  }
</script>

<!-- AGE-PILL-ATTR-ANCHOR: M-NO-ATTR removes data-age-tier. -->
<span
  class="age-pill {tier}"
  data-age-s={integerAge ?? "NONE"}
  data-age-budget-s={budgetS ?? "NONE"}
  data-age-tier={tier}
  data-age-clock={age.clock}
  data-door={door}
  data-root={servedRoot}
  aria-label={shown}>{shown}</span>

<style>
  .age-pill {
    display: inline-flex;
    align-items: center;
    min-height: 16px;
    box-sizing: border-box;
    border: 1px solid var(--line);
    border-radius: 999px;
    padding: 1px 6px;
    color: var(--muted);
    background: var(--surface-2);
    font: 600 10px/1.2 var(--font-num);
    white-space: nowrap;
  }
  .age-pill.amber {
    color: var(--stale-ink);
    background: var(--stale-soft);
    border-color: var(--stale);
    border-style: dashed;
    font-weight: 700;
  }
  .age-pill.red {
    color: var(--age-red-ink);
    background: var(--age-red-soft);
    border-color: var(--age-red);
    border-style: solid;
    border-width: 2px;
    font-weight: 800;
  }
  .age-pill.unknown {
    border-color: var(--age-red);
    border-style: double;
    color: var(--age-red-ink);
    background: var(--age-red-soft);
    font-weight: 800;
  }
</style>
